import { useCallback, useMemo, useRef, useState } from "react";

import { getUploadUrl, uploadFileToS3 } from "./imageImportApi";
import { resolveFileType, validateSkuFile } from "./skuFile";

// How many files upload to S3 at once. Keeps thousands of files moving without
// opening thousands of sockets.
const CONCURRENCY = 4;

// File status: pending | uploading | done | error. `invalid` marks a file that
// failed client-side validation and can never upload until removed/renamed.
const makeEntry = (file, id) => {
  const clientError = validateSkuFile(file);
  return {
    id,
    file,
    name: file.name,
    size: file.size,
    fileType: resolveFileType(file),
    status: clientError ? "error" : "pending",
    invalid: Boolean(clientError),
    progress: 0,
    error: clientError,
  };
};

/**
 * Owns the selected-file list and uploads each file straight to S3 via a
 * presigned URL. Validation, per-file progress, and retry of failed files are
 * all handled here; the job trigger/poll lives in useImageImport.
 */
export const useImageUpload = () => {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const filesRef = useRef(files);
  const idRef = useRef(0);

  // Keep a ref mirror so uploadAll() reads the latest list without stale closures.
  const commit = useCallback((updater) => {
    setFiles((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      filesRef.current = next;
      return next;
    });
  }, []);

  const patchFile = useCallback(
    (id, patch) =>
      commit((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f))),
    [commit]
  );

  const addFiles = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList);
      commit((prev) => {
        const existing = new Set(prev.map((f) => f.name));
        const entries = incoming
          .filter((file) => !existing.has(file.name)) // de-dupe by name
          .map((file) => makeEntry(file, (idRef.current += 1)));
        return [...prev, ...entries];
      });
    },
    [commit]
  );

  const removeFile = useCallback(
    (id) => commit((prev) => prev.filter((f) => f.id !== id)),
    [commit]
  );

  const clearFiles = useCallback(() => commit([]), [commit]);

  const uploadOne = useCallback(
    async (entry) => {
      patchFile(entry.id, { status: "uploading", progress: 0, error: null });
      try {
        const { upload_url: uploadUrl } = await getUploadUrl(
          entry.name,
          entry.fileType
        );
        await uploadFileToS3(uploadUrl, entry.file, entry.fileType, (p) =>
          patchFile(entry.id, { progress: p })
        );
        patchFile(entry.id, { status: "done", progress: 100 });
      } catch (err) {
        patchFile(entry.id, {
          status: "error",
          error:
            err?.response?.data?.message || err?.message || "Upload failed.",
        });
      }
    },
    [patchFile]
  );

  /**
   * Uploads every valid file that isn't already done (so it also retries
   * previously-failed files). Resolves to true only when every file is `done`.
   */
  const uploadAll = useCallback(async () => {
    const queue = filesRef.current.filter(
      (f) => !f.invalid && f.status !== "done"
    );
    if (queue.length === 0) {
      return filesRef.current.length > 0 && filesRef.current.every((f) => f.status === "done");
    }

    setIsUploading(true);
    const worker = async () => {
      let next;
      while ((next = queue.shift())) {
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(next);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker)
    );
    setIsUploading(false);

    return filesRef.current.every((f) => f.status === "done");
  }, [uploadOne]);

  const stats = useMemo(() => {
    const done = files.filter((f) => f.status === "done").length;
    const failed = files.filter((f) => f.status === "error").length;
    const uploadableFiles = files.filter((f) => !f.invalid);
    const uploadable = uploadableFiles.length;
    // Aggregate 0–100% across all uploadable files (a done file counts as 100).
    const uploadPct = uploadable
      ? Math.round(
          uploadableFiles.reduce(
            (sum, f) => sum + (f.status === "done" ? 100 : f.progress),
            0
          ) / uploadable
        )
      : 0;
    return {
      total: files.length,
      done,
      failed,
      uploadable,
      uploadPct,
      allDone: files.length > 0 && done === files.length,
      canStart: uploadable > 0 && !isUploading,
    };
  }, [files, isUploading]);

  return {
    files,
    isUploading,
    addFiles,
    removeFile,
    clearFiles,
    uploadAll,
    stats,
  };
};
